/**
 * The firework trigger, as context.
 *
 * Separate from `Fireworks.tsx` so that file exports components only — React Fast Refresh
 * cannot hot-swap a module that also exports hooks or plain functions (D48).
 */

import { createContext, useContext } from "react";

export type BurstFn = (
  x: number,
  y: number,
  energy: number | null,
  /** 1 is a task-sized burst. Projects use 3, which is what makes them feel different. */
  scale?: number,
) => void;

/** How much bigger a project's fireworks are than a task's. */
export const PROJECT_SCALE = 3;

/** Shots fired when you actually finish a project, as opposed to hovering the record of it. */
export const PROJECT_VOLLEY = 15;

export const FireworksContext = createContext<BurstFn>(() => {});

export function useFireworks(): BurstFn {
  return useContext(FireworksContext);
}

/**
 * Fire a burst repeatedly — once per point of activation cost.
 *
 * §8.4 wants completion to feel like something. Finishing a 5 sets off five bursts in
 * sequence; finishing a 1 sets off one. The work you avoided longest makes the most noise
 * when it finally goes.
 */
export function celebrate(burst: BurstFn, x: number, y: number, energy: number | null): void {
  const times = energy === null ? 1 : Math.max(1, Math.min(energy, 5));
  for (let i = 0; i < times; i++) {
    window.setTimeout(
      () =>
        burst(x + (Math.random() - 0.5) * 40 * i, y + (Math.random() - 0.5) * 30 * i, energy),
      i * 240,
    );
  }
}

/**
 * The celebration for finishing a *project* (§8.4).
 *
 * Deliberately on a different scale from a task. A project is weeks or months of work, and
 * a burst indistinguishable from ticking off "put the bins out" would say the wrong thing
 * about what just happened.
 *
 * Full-height, spread across the width, staggered so it reads as a volley rather than one
 * bang — and every layer, because there is no activation cost to scale by and this is the
 * top of the scale regardless.
 */
/**
 * Finishing a project.
 *
 * `shots` is the whole difference between doing it and remembering it: 15 where you
 * actually tick the project off, 5 for the echo when you run the cursor down the wins list.
 * Same scale either way — a project is bigger than a task by size, not by repetition — but
 * the real thing goes on long enough to be an event.
 */
export function celebrateProject(burst: BurstFn, shots = 5): void {
  const width = window.innerWidth;
  const height = window.innerHeight;

  for (let i = 0; i < shots; i++) {
    window.setTimeout(
      () =>
        burst(
          width * (0.1 + Math.random() * 0.8),
          height * (0.15 + Math.random() * 0.55),
          5,
          PROJECT_SCALE,
        ),
      i * 170,
    );
  }
}
